import { Injectable, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, catchError, throwError, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PromotionV2 } from '../../shared/models/promotions-v2/promotion-v2.model';
import { PromotionV2Mapper } from '../../shared/models/promotions-v2/promotion-v2.mapper';
import { ProposalV2 } from '../../shared/models/promotions-v2/proposal-v2.model';
import { ProposalV2Mapper } from '../../shared/models/promotions-v2/proposal-v2.mapper';
import { SharedService } from '../../shared/service/shared.service';
import {
  PROPOSAL_ACTION,
  ProposalAction,
  PROMOTION_ACTION,
  PromotionAction,
} from '../../shared/models/promotions-v2/workflow-action.model';

export interface V2ApiResponse<T> {
  code: number;
  status: boolean;
  result?: T;
  data?: T;
  error?: string;
  message?: string;
  summary?: {
    total: number;
    approved: number;
    blocked: number;
  };
}

export interface PromotionApprovalItemResult {
  promotion_id: number;
  p_status_id: string;
  status: string;
  message: string;
  violations: any[];
  conflict_details?: any[];
}

export interface ProposalSubmissionItemResult {
  proposal_id: number;
  p_status_id: string;
  status: string;
  message: string;
  violations: any[];
  conflict_details?: any[];
}

export interface ProposalAcceptanceItemResult {
  proposal_id: number;
  p_status_id: string;
  status: string;
  message: string;
  violations: any[];
  conflict_details?: any[];
  promotion?: Array<{
    promotion_id: number;
    message: string;
    p_status_id: string;
  }>;
}

export type ProposalApprovalItemResult = ProposalAcceptanceItemResult;

@Injectable({
  providedIn: 'root'
})
export class SummaryV2Service {
  constructor(
    private readonly http: HttpClient,
    @Optional() private readonly sharedService?: SharedService
  ) {}

  /**
   * Base URL for V2 promotions REST endpoint (e.g. http://localhost:6020/api/v2/promotions)
   */
  public get promotionsUrl(): string {
    return `${environment.v2BaseUrl}/promotions`;
  }

  /**
   * Base URL for V2 proposals REST endpoint (e.g. http://localhost:6020/api/v2/proposals)
   */
  public get proposalsUrl(): string {
    return `${environment.v2BaseUrl}/proposals`;
  }

  /**
   * Creates a promotion using clean V2 REST payload.
   */
  public createPromotion(promotion: PromotionV2): Observable<PromotionV2> {
    return this.http
      .post<V2ApiResponse<PromotionV2>>(this.promotionsUrl, promotion)
      .pipe(
        map((response) => (response.result ?? response.data ?? response) as PromotionV2),
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Updates an existing promotion using clean V2 REST payload.
   */
  public updatePromotion(id: number, promotion: PromotionV2): Observable<PromotionV2> {
    return this.http
      .put<V2ApiResponse<PromotionV2>>(`${this.promotionsUrl}/${id}`, promotion)
      .pipe(
        map((response) => (response.result ?? response.data ?? response) as PromotionV2),
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Creates a proposal using clean V2 REST payload.
   */
  public createProposal(proposal: ProposalV2 | PromotionV2): Observable<ProposalV2> {
    return this.http
      .post<V2ApiResponse<ProposalV2>>(this.proposalsUrl, proposal)
      .pipe(
        map((response) => (response.result ?? response.data ?? response) as ProposalV2),
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Updates an existing proposal using clean V2 REST payload.
   */
  public updateProposal(id: number, proposal: ProposalV2 | PromotionV2): Observable<ProposalV2> {
    return this.http
      .put<V2ApiResponse<ProposalV2>>(`${this.proposalsUrl}/${id}`, proposal)
      .pipe(
        map((response) => (response.result ?? response.data ?? response) as ProposalV2),
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Preview in-memory valuation of a promotion without persisting.
   */
  public previewPromotion(promotion: PromotionV2): Observable<PromotionV2> {
    return this.http
      .post<V2ApiResponse<PromotionV2>>(`${this.promotionsUrl}/preview`, promotion)
      .pipe(
        map((response) => (response.result ?? response.data ?? response) as PromotionV2),
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Preview in-memory valuation of a proposal without persisting.
   */
  public previewProposal(proposal: ProposalV2 | PromotionV2): Observable<ProposalV2> {
    return this.http
      .post<V2ApiResponse<ProposalV2>>(`${this.proposalsUrl}/preview`, proposal)
      .pipe(
        map((response) => (response.result ?? response.data ?? response) as ProposalV2),
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Generic router for saving either a Promotion or Proposal based on entity type and ID.
   */
  public saveEntity(
    entity: PromotionV2 | ProposalV2,
    entityType: 'PRM' | 'PRP'
  ): Observable<PromotionV2 | ProposalV2> {
    const id =
      entityType === 'PRP'
        ? (entity as ProposalV2).proposal_id ?? entity.id
        : (entity as PromotionV2).promotion_id ?? entity.id;

    if (entityType === 'PRP') {
      return id != null && id > 0
        ? this.updateProposal(id, entity as ProposalV2)
        : this.createProposal(entity as ProposalV2);
    }
    return id != null && id > 0
      ? this.updatePromotion(id, entity as PromotionV2)
      : this.createPromotion(entity as PromotionV2);
  }

  /**
   * Run commercial conflict check for a single promotion.
   */
  public checkConflicts(promotionId: number): Observable<any> {
    return this.http
      .post<V2ApiResponse<any>>(`${this.promotionsUrl}/${promotionId}/conflict-check`, {})
      .pipe(
        map((response) => response.result ?? response.data ?? response),
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Run bulk commercial conflict check for multiple promotions.
   */
  public bulkCheckConflicts(promotionIds: number[]): Observable<any> {
    return this.http
      .post<V2ApiResponse<any>>(`${this.promotionsUrl}/conflict-check`, { promotion_ids: promotionIds })
      .pipe(
        map((response) => response.result ?? response.data ?? response),
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Execute atomic multi-step approval workflow for a single promotion.
   */
  public approvePromotion(promotionId: number): Observable<any> {
    return this.http
      .post<V2ApiResponse<any>>(`${this.promotionsUrl}/${promotionId}/approve`, {})
      .pipe(
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Bulk Approve Promotions via high-performance V2 superhighway.
   */
  public bulkApprovePromotions(promotionIds: number[]): Observable<any> {
    return this.http
      .post<V2ApiResponse<any>>(`${this.promotionsUrl}/bulk-approve`, { promotion_ids: promotionIds })
      .pipe(
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Delete single promotion (DELETE /api/v2/promotions/:id)
   */
  public deletePromotion(promotionId: number): Observable<any> {
    return this.http
      .delete<V2ApiResponse<any>>(`${this.promotionsUrl}/${promotionId}`)
      .pipe(
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Bulk Delete Promotions via high-performance V2 batch endpoint (POST /api/v2/promotions/bulk-delete)
   */
  public bulkDeletePromotions(
    promotionIds: number[],
    action?: PromotionAction | string,
    promotionData?: any[]
  ): Observable<any> {
    const payload: any = { promotion_ids: promotionIds };
    if (action) {
      payload.action = action;
    }
    if (promotionData && promotionData.length > 0) {
      payload.promotion_data = promotionData;
    }
    return this.http
      .post<V2ApiResponse<any>>(`${this.promotionsUrl}/bulk-delete`, payload)
      .pipe(
        catchError((err) => throwError(() => err))
      );
  }


  /**
   * Handles bulk approval for V2 promotions, translating the V2 approval workflow
   * result into the legacy envelope expected by master-grid.component.ts.
   */
  public handleBulkApproval(promotions: any[]): Observable<any> {
    const promoIds = promotions
      .map((p: any) => p.promotion_id ?? p.id)
      .filter((id: any) => id != null && !isNaN(Number(id)))
      .map((id: any) => Number(id));

    if (promoIds.length === 0) {
      return of({
        code: 400,
        status: false,
        result: [],
        message: 'No valid promotion IDs provided for approval',
      });
    }

    return this.bulkApprovePromotions(promoIds).pipe(
      tap(() => {
        if (this.sharedService) {
          promotions.forEach((e: any) => this.sharedService?.resetSessionStorageSearch(e.promotion_id ?? e.id));
        }
      }),
      map((res: any) => {
        const rawResults: any[] = res?.result ?? (Array.isArray(res) ? res : (res?.data?.results || [res]));
        const isSuccess = (typeof res?.status === 'boolean' ? res.status : true)
          && (res?.summary ? res.summary.blocked === 0 : true)
          && !rawResults.some((r: any) => r.message === 'FAILED' || (r.violations && r.violations.length > 0));

        return {
          code: 200,
          status: isSuccess,
          result: promotions.map((p: any) => {
            const promoId = Number(p.promotion_id ?? p.id);
            const promoRes = rawResults.find((r: any) => Number(r.promotion_id ?? r.promotionId ?? r.id) === promoId)
              ?? res?.data?.conflictResults?.find((r: any) => Number(r.promotion_id ?? r.promotionId) === promoId);
            const isApproved = promoRes
              ? (promoRes.message === 'SUCCESS' && (!promoRes.violations || promoRes.violations.length === 0))
              : isSuccess;
            const violations = promoRes?.violations || [];
            return {
              ...p,
              promotion_id: promoId,
              message: isApproved ? 'SUCCESS' : 'FAILED',
              p_status_id: isApproved ? 'Approved' : (p.p_status_id || 'Draft'),
              status: isApproved ? 'Approved' : 'Conflict check failed.',
              violations,
              conflict_details: violations,
            };
          }),
          summary: res?.summary,
        };
      }),
      catchError((err: any) => {
        const conflictData = err?.error?.data ?? err?.error;
        return of({
          code: 200,
          status: false,
          result: promotions.map((p: any) => {
            const promoId = Number(p.promotion_id ?? p.id);
            const promoRes = conflictData?.results?.find((r: any) => Number(r.promotion_id ?? r.promotionId) === promoId)
              ?? conflictData?.conflictResults?.find((r: any) => Number(r.promotion_id ?? r.promotionId) === promoId);
            const violations = promoRes?.violations || [];
            return {
              ...p,
              promotion_id: promoId,
              message: 'FAILED',
              p_status_id: p.p_status_id || 'Draft',
              status: 'Conflict check failed.',
              violations,
              conflict_details: violations,
            };
          }),
          error: err?.error?.message || err?.message,
        });
      })
    );
  }

  /**
   * Run commercial conflict check for a single proposal.
   */
  public checkProposalConflicts(proposalId: number, role: 'BUYER' | 'SUPPLIER' = 'BUYER'): Observable<any> {
    return this.http
      .post<V2ApiResponse<any>>(`${this.proposalsUrl}/${proposalId}/conflict-check`, { role })
      .pipe(
        map((response) => response.result ?? response.data ?? response),
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Run bulk commercial conflict check for multiple proposals.
   */
  public bulkCheckProposalConflicts(proposalIds: number[], role: 'BUYER' | 'SUPPLIER' = 'BUYER'): Observable<any> {
    return this.http
      .post<V2ApiResponse<any>>(`${this.proposalsUrl}/conflict-check`, { proposal_ids: proposalIds, role })
      .pipe(
        map((response) => response.result ?? response.data ?? response),
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Run bulk Price Establishment pre-flight validation for multiple proposals.
   */
  public bulkCheckProposalPEValidation(proposalIds: number[], role: 'BUYER' | 'SUPPLIER' = 'SUPPLIER'): Observable<any> {
    return this.http
      .post<V2ApiResponse<any>>(`${this.proposalsUrl}/pe-validation`, { proposal_ids: proposalIds, role })
      .pipe(
        map((response) => response.result ?? response.data ?? response),
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Submit a single proposal (Draft -> Submitted).
   */
  public submitProposal(proposalId: number): Observable<any> {
    return this.http
      .post<V2ApiResponse<any>>(`${this.proposalsUrl}/${proposalId}/submit`, {})
      .pipe(
        map((response) => response.result ?? response.data ?? response),
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Bulk submit proposals (Draft -> Submitted).
   */
  public bulkSubmitProposals(proposalIds: number[]): Observable<any> {
    return this.http
      .post<V2ApiResponse<any>>(`${this.proposalsUrl}/bulk-submit`, { proposal_ids: proposalIds })
      .pipe(
        map((response) => response.result ?? response.data ?? response),
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Accept a single proposal (Submitted -> Accepted) and create promotion.
   */
  public acceptProposal(proposalId: number, action: 'Accept_Draft' | 'Accept_Approve'): Observable<any> {
    return this.http
      .post<V2ApiResponse<any>>(`${this.proposalsUrl}/${proposalId}/accept`, { action })
      .pipe(
        map((response) => response.result ?? response.data ?? response),
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Bulk accept proposals (Submitted -> Accepted) and create promotions.
   */
  public bulkAcceptProposals(proposalIds: number[], action: 'Accept_Draft' | 'Accept_Approve'): Observable<any> {
    return this.http
      .post<V2ApiResponse<any>>(`${this.proposalsUrl}/bulk-accept`, { proposal_ids: proposalIds, action })
      .pipe(
        map((response) => response.result ?? response.data ?? response),
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Bulk reject proposals (Submitted -> Rejected).
   */
  public bulkRejectProposals(
    proposalIds: number[],
    rejectReasonId: string,
    rejectReasonDescription?: string | null
  ): Observable<any> {
    return this.http
      .post<V2ApiResponse<any>>(`${this.proposalsUrl}/bulk-reject`, {
        proposal_ids: proposalIds,
        reject_reason_id: rejectReasonId,
        reject_reason_description: rejectReasonDescription || null,
      })
      .pipe(
        map((response) => response.result ?? response.data ?? response),
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Delete a single proposal (Draft/Withdrawn/Rejected -> Deleted).
   */
  public deleteProposal(proposalId: number): Observable<any> {
    return this.http
      .delete<V2ApiResponse<any>>(`${this.proposalsUrl}/${proposalId}`)
      .pipe(
        map((response) => response.result ?? response.data ?? response),
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Bulk delete proposals (Draft/Withdrawn/Rejected -> Deleted).
   */
  public bulkDeleteProposals(proposalIds: number[]): Observable<any> {
    return this.http
      .post<V2ApiResponse<any>>(`${this.proposalsUrl}/bulk-delete`, { proposal_ids: proposalIds })
      .pipe(
        map((response) => response.result ?? response.data ?? response),
        catchError((err) => throwError(() => err))
      );
  }

  /**
   * Returns true if the proposal action has a dedicated V2 workflow implementation.
   * Actions not yet migrated (e.g. Withdraw, Bulk_Edit, Labels) return false to allow legacy fallback.
   */
  public canHandleProposalAction(action: ProposalAction | string): boolean {
    const supportedActions: string[] = [
      PROPOSAL_ACTION.SUBMIT,
      PROPOSAL_ACTION.ACCEPT_DRAFT,
      PROPOSAL_ACTION.ACCEPT_APPROVE,
      PROPOSAL_ACTION.REJECT,
      PROPOSAL_ACTION.DELETE,
      PROPOSAL_ACTION.SAVE,
    ];
    return supportedActions.includes(action);
  }

  /**
   * Returns true if the promotion action has a dedicated V2 workflow implementation.
   * Actions not yet migrated return false to allow legacy fallback.
   */
  public canHandlePromotionAction(action: PromotionAction | string): boolean {
    const supportedActions: string[] = [
      PROMOTION_ACTION.APPROVE,
      PROMOTION_ACTION.SAVE,
      PROMOTION_ACTION.DELETE,
      PROMOTION_ACTION.DELETE_REJECT,
    ];
    return supportedActions.includes(action);
  }

  /**
   * Handles proposal lifecycle actions for V2 proposals,
   * translating results to the legacy response envelope required by master-grid.component.ts.
   * Relies strictly on ProposalAction to avoid errant fall-through.
   */
  public handleProposalAction(
    proposals: any[],
    action: ProposalAction | string,
    peValidationEndpoint: boolean = false
  ): Observable<any> {
    const proposalIds = proposals
      .map((p: any) => p.proposal_id ?? p.id)
      .filter((id: any) => id != null && !isNaN(Number(id)))
      .map((id: any) => Number(id));

    if (proposalIds.length === 0) {
      return of({
        code: 400,
        status: false,
        result: [],
        message: 'No valid proposal IDs provided',
      });
    }

    switch (action) {
      case PROPOSAL_ACTION.SUBMIT: {
        if (peValidationEndpoint) {
          return this.bulkCheckProposalPEValidation(proposalIds, 'SUPPLIER').pipe(
            map((resp: any) => {
              const peResults = resp.result ?? resp.data ?? resp;
              return {
                code: 200,
                status: true,
                result: proposals.map((p: any) => {
                  const pid = Number(p.proposal_id ?? p.id);
                  const pe = Array.isArray(peResults)
                    ? peResults.find((r: any) => (r.proposal_id ?? r.id) === pid)
                    : null;
                  const warningCount = pe?.warning_count ?? 0;
                  return {
                    ...p,
                    warning_count: warningCount,
                    status: pe?.status ?? (warningCount > 0 ? 'PE_VALIDATION_CONTAINS_WARNING' : 'PE_VALIDATION_SUCCESS'),
                  };
                }),
              };
            }),
            catchError((err: any) => of({ code: 500, status: false, result: [], error: err }))
          );
        }

        return this.bulkSubmitProposals(proposalIds).pipe(
          tap(() => {
            if (this.sharedService) {
              proposals.forEach((p: any) => this.sharedService?.resetSessionStorageSearch(p.proposal_id ?? p.id));
            }
          }),
          map((resp: any) => {
            const results = resp.result ?? resp.data ?? resp;
            const mappedResults = Array.isArray(results)
              ? results.map((r: any) => ({
                  ...r,
                  conflict_details: r.violations || r.conflict_details || [],
                }))
              : results;
            return {
              code: 200,
              status: resp?.status ?? true,
              result: mappedResults,
              summary: resp?.summary,
            };
          }),
          catchError((err: any) => of({ code: 500, status: false, result: [], error: err }))
        );
      }

      case PROPOSAL_ACTION.ACCEPT_DRAFT:
      case PROPOSAL_ACTION.ACCEPT_APPROVE: {
        return this.bulkAcceptProposals(proposalIds, action as 'Accept_Draft' | 'Accept_Approve').pipe(
          tap(() => {
            if (this.sharedService) {
              proposals.forEach((p: any) => this.sharedService?.resetSessionStorageSearch(p.proposal_id ?? p.id));
            }
          }),
          map((resp: any) => {
            const results = resp.result ?? resp.data ?? resp;
            const mappedResults = Array.isArray(results)
              ? results.map((r: any) => ({
                  ...r,
                  conflict_details: r.violations || r.conflict_details || [],
                }))
              : results;
            return {
              code: 200,
              status: resp?.status ?? true,
              result: mappedResults,
              summary: resp?.summary,
            };
          }),
          catchError((err: any) => of({ code: 500, status: false, result: [], error: err }))
        );
      }

      case PROPOSAL_ACTION.REJECT: {
        const rejectReasonId = proposals[0]?.reject_reason_id || 'PRP_REASON6';
        const rejectReasonDescription = proposals[0]?.reject_reason_description || null;
        return this.bulkRejectProposals(proposalIds, rejectReasonId, rejectReasonDescription).pipe(
          tap(() => {
            if (this.sharedService) {
              proposals.forEach((p: any) => this.sharedService?.resetSessionStorageSearch(p.proposal_id ?? p.id));
            }
          }),
          map((resp: any) => {
            const results = resp.result ?? resp.data ?? resp;
            return {
              code: 200,
              status: resp?.status ?? true,
              result: Array.isArray(results) ? results : [results],
              summary: resp?.summary,
            };
          }),
          catchError((err: any) => of({ code: 500, status: false, result: [], error: err }))
        );
      }

      case PROPOSAL_ACTION.DELETE: {
        return this.bulkDeleteProposals(proposalIds).pipe(
          tap(() => {
            if (this.sharedService) {
              proposals.forEach((p: any) => this.sharedService?.resetSessionStorageSearch(p.proposal_id ?? p.id));
            }
          }),
          map((resp: any) => {
            const results = resp.result ?? resp.data ?? resp;
            return {
              code: 200,
              status: resp?.status ?? true,
              result: Array.isArray(results) ? results : [results],
              summary: resp?.summary,
            };
          }),
          catchError((err: any) => of({ code: 500, status: false, result: [], error: err }))
        );
      }

      case PROPOSAL_ACTION.SAVE: {
        return this.handleSave(proposals, 'PRP');
      }

      default: {
        throw new Error(`Unhandled proposal action in V2 SummaryV2Service: ${action}`);
      }
    }
  }

  /**
   * Orchestrates batch promotion workflow actions from Summary screen.
   * Relies strictly on PromotionAction to avoid errant fall-through.
   */
  public handlePromotionAction(
    promotions: any[],
    action: PromotionAction | string
  ): Observable<any> {
    const promotionIds = promotions
      .map((p: any) => p.promotion_id ?? p.id)
      .filter((id: any) => id != null && !isNaN(Number(id)))
      .map((id: any) => Number(id));

    if (promotionIds.length === 0) {
      return of({
        code: 400,
        status: false,
        result: [],
        message: 'No valid promotion IDs provided',
      });
    }

    switch (action) {
      case PROMOTION_ACTION.APPROVE: {
        return this.handleBulkApproval(promotions);
      }

      case PROMOTION_ACTION.SAVE: {
        return this.handleSave(promotions, 'PRM');
      }

      case PROMOTION_ACTION.DELETE:
      case PROMOTION_ACTION.DELETE_REJECT: {
        return this.bulkDeletePromotions(promotionIds, action, promotions).pipe(
          tap(() => {
            if (this.sharedService) {
              promotions.forEach((p: any) =>
                this.sharedService?.resetSessionStorageSearch(p.promotion_id ?? p.id)
              );
            }
          }),
          map((resp: any) => {
            const results = resp.result ?? resp.data ?? resp;
            return {
              code: 200,
              status: resp?.status ?? true,
              result: Array.isArray(results) ? results : [results],
              summary: resp?.summary,
            };
          }),
          catchError((err: any) => of({ code: 500, status: false, result: [], error: err }))
        );
      }

      default: {
        throw new Error(`Unhandled promotion action in V2 SummaryV2Service: ${action}`);
      }
    }
  }


  /**
   * Handles draft saving for V2 promotions/proposals, mapping to clean V2 model
   * and wrapping the result into the legacy envelope expected by master-grid.
   */
  public handleSave(entities: any[], entityType: 'PRM' | 'PRP'): Observable<any> {
    const isProposal = entityType === 'PRP';
    const cleanEntity = isProposal
      ? ProposalV2Mapper.toProposalV2(entities[0])
      : PromotionV2Mapper.toPromotionV2(entities[0]);

    return this.saveEntity(cleanEntity, entityType).pipe(
      tap(() => {
        if (this.sharedService) {
          entities.forEach((e: any) =>
            this.sharedService?.resetSessionStorageSearch(e.proposal_id ?? e.promotion_id)
          );
        }
      }),
      map((savedV2) => ({
        code: 200,
        status: true,
        result: [
          isProposal
            ? ProposalV2Mapper.toLegacyProposal(savedV2 as ProposalV2)
            : PromotionV2Mapper.toLegacyPromotion(savedV2 as PromotionV2),
        ],
      })),
      catchError((err) => of({ result: {} as any, code: 'error', error: err }))
    );
  }
}

